import { Editor, JSONContent } from '@tiptap/core';
import { Mark } from 'prosemirror-model';

import { getFirstCharacterTextMarks, getFontStyleFromTextMarks } from '../TextWidget.helpers';
import { Letter, LetterStyle, TextTransformation } from './CurvedText.types';

const INITIAL_DIAMETER_PX = 500;
export const radiansToDegrees = (radians: number) => radians * (180 / Math.PI);
export const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180);

/**
 * Compute the outer diameter of the circle based on the font size and curvature.
 * Max diameter is 4x the font size.
 *
 * @param curvature
 * @param fontSize
 * @returns
 */
export const computeDiameterFromFontSize = (curvature: number, fontSize: number) => {
  if (curvature === 0) return Infinity;
  const diameter = (400 / Math.abs(curvature)) * fontSize;
  return diameter;
};

/**
 * Get the angle of rotation for each letter to form the curve and the total angle for all letters
 *
 * @param metrics
 * @param r
 * @returns
 */
export const getLetterRotations = (metrics: { width: number }[], r: number) =>
  metrics.reduce(
    (data, { width }) => {
      const rotation = radiansToDegrees(width / r);
      return {
        theta: data.theta + rotation,
        rotations: data.rotations.concat([data.theta + rotation / 2]),
      };
    },
    { theta: 0, rotations: [] as number[] },
  );

/**
 * Convert ProseMirror marks to letter styles
 *
 * @param marks
 * @returns
 */
export const convertMarkToStyle = (marks: readonly Mark[] | JSONContent['marks']): LetterStyle => {
  if (!marks?.length) return {};

  const letterStyle: LetterStyle = (marks as Mark[]).reduce((acc, mark) => {
    const markType = (mark as Mark)?.type?.name || mark?.type;

    switch (markType) {
      case 'bold':
        acc.isBold = true;
        break;
      case 'italic':
        acc.isItalic = true;
        break;
      case 'underline':
        acc.isUnderline = true;
        break;
      case 'link':
        const { href } = mark?.attrs || {};
        if (href) {
          acc.hyperlink = href;
        }
        break;
      case 'textStyle':
        const { color } = mark?.attrs || {};
        if (color) {
          acc.color = color;
        }
        break;
    }

    return acc;
  }, {} as LetterStyle);

  return letterStyle;
};

/**
 * Convert ProseMirror **editor** to letter styles for each letter based on marks
 * @param editor
 */
export const getLetterStyleFromPMEditor = (editor: Editor | null) => {
  if (!editor) return [];

  const { doc } = editor.state;
  const letterStyles = [] as Letter[];

  // iterate over all text nodes
  doc.nodesBetween(0, doc.content.size, (node) => {
    if (node.isText) {
      const { marks } = node || {};
      const letterStyle = convertMarkToStyle(marks);
      const text = node.text;
      if (!text) {
        return;
      }
      letterStyles.push(...Array.from(text).map((char) => ({ char, letterStyle })));
    }
  });

  return letterStyles;
};

/**
 * Convert ProseMirror JSON to letter styles for each letter based on marks
 *
 * @param node
 * @returns
 */
const getLetterStyleFromPMJson = (node: JSONContent | null) => {
  if (!node) return [];
  const letterStyles: Letter[] = [];
  if (node.type === 'text' && node.text) {
    const { marks, text } = node;
    const letterStyle = convertMarkToStyle(marks);
    letterStyles.push(...Array.from(text).map((char) => ({ char, letterStyle })));
  }
  if (node.content) {
    node.content.forEach((child) => {
      letterStyles.push(...getLetterStyleFromPMJson(child));
    });
  }

  return letterStyles;
};

/**
 * Generate text metrics for curved text based on ProseMirror JSON
 *
 * @param pmData
 * @param letterSpacing
 * @returns
 */
export const generateTextMetricsFromPMData = (pmData: JSONContent, letterSpacing: string) => {
  const letterStyles = getLetterStyleFromPMJson(pmData);
  const textMarks = getFirstCharacterTextMarks(pmData);
  const { fontSize, fontFamily } = getFontStyleFromTextMarks(textMarks);
  const fontSizePx = typeof fontSize === 'string' ? parseFloat(fontSize) : fontSize;
  return {
    ...generateTextMetrics(letterStyles, fontFamily, fontSizePx, letterSpacing),
    fontSize: fontSizePx,
  };
};

/**
 * Generate text metrics for curved text based on ProseMirror editor
 * Returns the width of each letter after styling is applied
 *
 * @param letterStyles
 * @param fontFamily
 * @param fontSize
 * @param letterSpacing
 * @returns
 */
export const generateTextMetrics = (
  letterStyles: Letter[],
  fontFamily: string,
  fontSize: number,
  letterSpacing: string,
) => {
  let totalWidth = 0;
  const metrics = letterStyles.map(({ char, letterStyle }) => {
    const { isBold, isItalic, isUnderline } = letterStyle;
    const span = document.createElement('span');

    span.style.fontSize = `${fontSize}px`;
    span.style.fontFamily = fontFamily;
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'pre';
    span.style.letterSpacing = letterSpacing || '0em';
    span.innerText = char;

    // Add styles for bold, italic, underline
    if (isBold) span.style.fontWeight = 'bold';
    if (isItalic) span.style.fontStyle = 'italic';
    if (isUnderline) span.style.textDecoration = 'underline';

    document.body.appendChild(span);
    const { width } = span.getBoundingClientRect();
    document.body.removeChild(span);
    totalWidth += width;
    return { width };
  });

  return { metrics, totalWidth };
};

/// Helpers for computing width/height of curved text
const sagitta = (r: number, theta: number) => r * (1 - Math.cos(degreesToRadians(theta / 2)));
const chord = (r: number, theta: number) => 2 * r * Math.sin(degreesToRadians(theta / 2));

/**
 * Compute the width and height of the curved text based on the curvature and font size
 * TODO - fix calculation for negative curvature
 *
 * @param curvature
 * @param fontSize
 * @param metrics
 * @returns
 */
export const computeCurveDimensions = (curvature: number, fontSize: number, metrics: { width: number }[]) => {
  const diameter = computeDiameterFromFontSize(curvature, fontSize);
  const radius = diameter / 2;
  const innerRadius = radius - fontSize;
  const { theta } = getLetterRotations(metrics, innerRadius);
  const descenderHeight = 0.4 * fontSize;

  return {
    width: chord(radius, Math.min(180, theta)),
    height:
      theta > 180
        ? sagitta(radius, Math.min(theta, 360))
        : sagitta(radius - fontSize, theta) + fontSize + descenderHeight,
  };
};

/**
 * Compute the transformations required for each letter based on the editor, style, diameter, and direction
 *
 * @param editor
 * @param style
 * @param diameter
 * @param dir
 * @returns
 */
export const generateCurvedText = (
  editor: Editor,
  style: { fontFamily: string; fontSizePx: string; letterSpacing: string },
  diameter = INITIAL_DIAMETER_PX,
  dir = 1,
): {
  width: number;
  height: number;
  textTransformations: TextTransformation[];
} => {
  const { fontFamily, fontSizePx, letterSpacing } = style;
  const fontSize = parseFloat(fontSizePx);
  const r = diameter / 2;
  const radius = dir === -1 ? -r + fontSize : r;
  const innerRadius = radius - fontSize;
  const textStyles = getLetterStyleFromPMEditor(editor);
  const isStraight = radius === Infinity;

  // Create an array of letter metrics with widths for each character
  const { totalWidth, metrics } = generateTextMetrics(textStyles, fontFamily, fontSize, letterSpacing);

  // Calculate the angle of rotation for each letter + total angle for all letters
  const { rotations, theta } = getLetterRotations(metrics, innerRadius);

  // Create an array of transformations for each letter
  let accWidth = 0;
  const transformations = textStyles.map(({ char, letterStyle }, index) => {
    const angle = isStraight ? 0 : theta * -0.5 + rotations[index];
    const translateX = isStraight ? accWidth - (totalWidth * 0.5) / fontSize : (metrics[index].width * -0.5) / fontSize;
    const transform = `translateX(${translateX}em) rotate(${angle}deg) `;
    accWidth += metrics[index].width / fontSize;
    return {
      char,
      transform,
      letterStyle,
    };
  });

  // Additional height required to account for letters that go below the baseline (i.e. j, g, y, etc.)
  const descenderHeight = 0.4 * fontSize;
  return {
    width: chord(r, Math.min(180, theta)),
    height: theta > 180 ? sagitta(r, Math.min(theta, 360)) : sagitta(r - fontSize, theta) + fontSize + descenderHeight,
    textTransformations: transformations,
  };
};
